/**
 * Placeholder substitution for message templates.
 *
 * Deliberately the smallest thing that works: no expression syntax, no
 * pluralisation engine, no HTML. Templates are data and the result is plain
 * text that React escapes on render, so a malicious catalogue entry — or a
 * label the user typed — can only ever produce more text.
 */
import type { TranslationValues } from './types';

/**
 * Matches, in priority order: an escaped `{{`, an escaped `}}`, or a
 * `{placeholder}` whose name contains no further braces.
 */
const TOKEN = /\{\{|\}\}|\{([^{}]*)\}/g;

/**
 * Substitutes `{name}` placeholders with `values[name]`.
 *
 * Unknown placeholders are left exactly as written — a half-translated string
 * should still read as something, and a missing value is a catalogue bug worth
 * seeing rather than an exception in the render path. `{{` and `}}` produce
 * literal braces, which is how a message shows pseudocode. Substituted values
 * are never re-scanned, so a user-supplied name containing `{...}` cannot
 * inject another placeholder.
 */
export function interpolate(template: string, values?: TranslationValues): string {
  if (template.length === 0) return template;

  // Fast path: most messages have nothing to substitute.
  if (!template.includes('{') && !template.includes('}')) return template;

  return template.replace(TOKEN, (match, rawName?: string) => {
    if (match === '{{') return '{';
    if (match === '}}') return '}';

    const name = rawName === undefined ? '' : rawName.trim();
    if (name.length === 0 || values === undefined) return match;

    if (!Object.prototype.hasOwnProperty.call(values, name)) return match;
    const value = values[name];

    // Guards against inherited members leaking through a plain object cast.
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return Number.isFinite(value) ? String(value) : match;
    return match;
  });
}

/** The placeholder names a template expects, in order of first appearance. */
export function placeholderNames(template: string): readonly string[] {
  const names: string[] = [];
  for (const match of template.matchAll(TOKEN)) {
    const raw = match[1];
    if (raw === undefined) continue;
    const name = raw.trim();
    if (name.length > 0 && !names.includes(name)) names.push(name);
  }
  return names;
}
